/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { logger } from "@octopusdeploy/logging";
import { Environment, OctoLink } from "@octopusdeploy/utilities";
import type { Context } from "@opentelemetry/api";
import type { Callback } from "./SubscriptionRecord";
import { SubscriptionRecord } from "./SubscriptionRecord";
import type { AjaxErrorResponseDetails, AjaxRequestDetails, AjaxResponseDetails } from "./ajax";
import { Ajax } from "./ajax";
import { Caching } from "./caching";
import type { ClientEvent } from "./events/ClientEvent";
import type { ClientEventBus, EventHandler } from "./events/ClientEventBus";
import { createClientEventBus } from "./events/ClientEventBus";
import type { FetchOptions } from "./fetch";
import { dispatchFetch } from "./fetch";
import { getResponseStream } from "./getResponseStream";
import { UserRepository } from "./repositories/userRepository";
import type { RouteArgs } from "./resolver";
import { Resolver } from "./resolver";
import { OctopusError } from "./resources";
import type { SpaceRootLinks, SpaceRootResource } from "./resources";
import type { GlobalRootLinks, RootResource } from "./resources/rootResource";
const apiLocation = "~/api";
export interface ClientConfiguration {
    serverEndpoint: string;
}
export interface ClientRequestDetails {
    correlationId: number;
    url: string;
    method: string;
}
export interface ClientResponseDetails {
    request: XMLHttpRequest;
    correlationId: number;
    url: string;
    method: string;
    statusCode: number;
}
export interface ClientErrorResponseDetails extends ClientResponseDetails {
    errorMessage: string;
    errors: string[];
}
export type GlobalAndSpaceRootLinks = keyof GlobalRootLinks | keyof SpaceRootLinks;
export interface RequestContext {
    correlationContext: RequestCorrelationContext;
    abortSignal: AbortSignal;
    openTelemetryContext?: Context;
}
/**
 * The key must be lower case and can additionally contain `.` and `_` only.
 * Properties that may be ambiguous about whether they originated from the frontend or backend
 * should start with `frontend.`. E.g. `frontend.user.id`.
 * Other properties that are not ambiguous can skip this prefix. E.g. `browser.name` or `screen.size`
 * These keys are included as part of header names, which are case insensitive (as per the HTTP spec).
 * They are also included as attributes on spans, which are lower case, namespaces (separated by `.`), and contain underscores.
 * When ingested by the API, the backend will lowercase these property names to ensure we get consistent results, regardless of the supplied casing.
 * This type encourages us to use lowercase property names on the frontend too, to reduce any confusion about typing discrepancies.
 */
export type RequestCorrelationContext = Record<Lowercase<string>, string>;
// The Octopus Client implements the low-level semantics of the Octopus Deploy REST API
export class Client {
    public static Create(configuration: ClientConfiguration, isAuthenticated: () => boolean, endSession: () => void) {
        logger.info("Creating Octopus client for endpoint: {endpoint}", { endpoint: configuration.serverEndpoint });
        const resolver = new Resolver(configuration.serverEndpoint);
        const clientSession = new ClientSession(new Caching(), isAuthenticated, endSession);
        return new Client(clientSession, resolver, null, null, null, createClientEventBus());
    }
    requestSubscriptions = new SubscriptionRecord<ClientRequestDetails>();
    responseSubscriptions = new SubscriptionRecord<ClientResponseDetails>();
    errorSubscriptions = new SubscriptionRecord<ClientErrorResponseDetails>();
    onRequestCallback: (details: ClientRequestDetails) => void = undefined!;
    onResponseCallback: (details: ClientResponseDetails) => void = undefined!;
    onErrorResponseCallback: (details: ClientErrorResponseDetails) => void = undefined!;
    private constructor(readonly session: ClientSession, private readonly resolver: Resolver, private rootDocument: RootResource | null, 
    /**
     * @deprecated Accessing the current spaceId from the client should be avoided. Please consider passing the spaceId as a prop through the page if it is available
     * as part of the current page route. This can be passed through from our routing infrastructure in allPageRegistrations. If the spaceId is not available for the current route
     * then it could potentially accessed from a space scoped resource i.e. `project.SpaceId`.
     */
    public spaceId: string | null, private spaceRootDocument: SpaceRootResource | null, private readonly eventBus: ClientEventBus) {
        this.resolver = resolver;
        this.rootDocument = rootDocument;
        this.spaceRootDocument = spaceRootDocument;
    }
    subscribeToRequests = (registrationName: string, callback: Callback<ClientRequestDetails>) => {
        return this.requestSubscriptions.subscribe(registrationName, callback);
    };
    subscribeToResponses = (registrationName: string, callback: Callback<ClientResponseDetails>) => {
        return this.responseSubscriptions.subscribe(registrationName, callback);
    };
    subscribeToErrors = (registrationName: string, callback: (details: ClientErrorResponseDetails) => void) => {
        return this.errorSubscriptions.subscribe(registrationName, callback);
    };
    setOnRequestCallback = (callback: (details: ClientRequestDetails) => void) => {
        this.onRequestCallback = callback;
    };
    setOnResponseCallback = (callback: (details: ClientResponseDetails) => void) => {
        this.onResponseCallback = callback;
    };
    setOnErrorResponseCallback = (callback: (details: ClientErrorResponseDetails) => void) => {
        this.onErrorResponseCallback = callback;
    };
    resolve = (path: string, uriTemplateParameters?: RouteArgs) => this.resolver.resolve(path, uriTemplateParameters);
    connect(requestContext: RequestContext, progressCallback: (message: string, error?: OctopusError) => void): Promise<void> {
        progressCallback("Checking your credentials. Please wait...");
        return new Promise((resolve, reject) => {
            if (this.rootDocument) {
                resolve();
                return;
            }
            const attempt = (success: any, fail: any) => {
                this.get(requestContext, apiLocation).then((root) => {
                    success(root);
                }, fail);
            };
            const onSuccess = (root: RootResource) => {
                this.rootDocument = root;
                resolve();
            };
            let fails = 0;
            const onFail = (err: any) => {
                if (err.StatusCode !== 503 && fails < 20) {
                    fails++;
                }
                const timeout = fails === 20 ? 5000 : 1000;
                if ((err.StatusCode === 0 || err.StatusCode === 503) && fails < 20) {
                    if (err.StatusCode === 503) {
                        progressCallback("Octopus Server unavailable.", err);
                    }
                    else if (err.StatusCode === 0) {
                        progressCallback("The Octopus Server does not appear to have started, trying again...", err);
                    }
                }
                else {
                    progressCallback("Unable to connect to the Octopus Server. Is your server online?", err);
                }
                setTimeout(() => {
                    attempt(onSuccess, onFail);
                }, timeout);
            };
            attempt(onSuccess, onFail);
        });
    }
    disconnect() {
        this.rootDocument = null;
        this.spaceId = null;
        this.spaceRootDocument = null;
    }
    async forSpace(requestContext: RequestContext, spaceId: string): Promise<Client> {
        const spaceRootResource = await this.get<SpaceRootResource>(requestContext, this.rootDocument!.Links["SpaceHome"], { spaceId });
        return new Client(this.session, this.resolver, this.rootDocument, spaceId, spaceRootResource, this.eventBus);
    }
    forSystem(): Client {
        return new Client(this.session, this.resolver, this.rootDocument, null, null, this.eventBus);
    }
    async switchToSpace(requestContext: RequestContext, spaceId: string): Promise<void> {
        this.spaceId = spaceId;
        this.spaceRootDocument = await this.get<SpaceRootResource>(requestContext, this.rootDocument!.Links["SpaceHome"], { spaceId: this.spaceId });
    }
    switchToSystem(): void {
        this.spaceId = null;
        this.spaceRootDocument = null;
    }
    get<TResource>(requestContext: RequestContext, path: string, args?: RouteArgs): Promise<TResource> {
        const url = this.resolveUrlWithSpaceId(path, args);
        return this.dispatchRequest(requestContext, "GET", url) as Promise<TResource>;
    }
    async *streamingGet<TEvent>(requestContext: RequestContext, path: string, args?: RouteArgs): AsyncIterable<TEvent> {
        const url = this.resolveUrlWithSpaceId(path, args);
        const response = await this.dispatchFetch({
            requestContext,
            url,
            method: "GET",
            accept: "text/event-stream",
        });
        for await (const event of getResponseStream<TEvent>(response)) {
            yield event;
        }
    }
    async *streamingPost<TEvent>(requestContext: RequestContext, path: string, body?: any, args?: RouteArgs): AsyncIterable<TEvent> {
        const url = this.resolveUrlWithSpaceId(path, args);
        const response = await this.dispatchFetch({
            requestContext,
            url,
            body,
            method: "POST",
            accept: "text/event-stream",
        });
        for await (const event of getResponseStream<TEvent>(response)) {
            yield event;
        }
    }
    getRaw(requestContext: RequestContext, path: string, args?: RouteArgs): Promise<string> {
        const url = this.resolve(path, args);
        return this.dispatchRequest(requestContext, "GET", url, undefined, true) as Promise<string>;
    }
    onAjaxRequest(ajaxDetails: AjaxRequestDetails) {
        const details = {
            correlationId: ajaxDetails.correlationId,
            url: ajaxDetails.url,
            method: ajaxDetails.method,
        };
        if (this.onRequestCallback) {
            this.onRequestCallback(details);
        }
        this.requestSubscriptions.notifyAll(details);
    }
    onAjaxResponse(ajaxDetails: AjaxResponseDetails) {
        const details = {
            request: ajaxDetails.request,
            correlationId: ajaxDetails.correlationId,
            url: ajaxDetails.url,
            method: ajaxDetails.method,
            statusCode: ajaxDetails.statusCode,
        };
        if (this.onResponseCallback) {
            this.onResponseCallback(details);
        }
        this.responseSubscriptions.notifyAll(details);
    }
    onAjaxErrorResponse(ajaxDetails: AjaxErrorResponseDetails) {
        const details = {
            request: ajaxDetails.request,
            correlationId: ajaxDetails.correlationId,
            url: ajaxDetails.url,
            method: ajaxDetails.method,
            statusCode: ajaxDetails.statusCode,
            errorMessage: ajaxDetails.errorMessage,
            errors: ajaxDetails.errors,
        };
        if (this.onErrorResponseCallback) {
            this.onErrorResponseCallback(details);
        }
        this.errorSubscriptions.notifyAll(details);
    }
    post<TReturn>(requestContext: RequestContext, path: string, resource?: any, args?: RouteArgs): Promise<TReturn> {
        const url = this.resolveUrlWithSpaceId(path, args);
        return this.dispatchRequest(requestContext, "POST", url, resource) as Promise<TReturn>;
    }
    create<TNewResource, TResource>(requestContext: RequestContext, path: string, resource: TNewResource, args: RouteArgs): Promise<TResource> {
        const url = this.resolve(path, args);
        return new Promise((resolve, reject) => {
            this.dispatchRequest(requestContext, "POST", url, resource).then((result: any) => {
                const selfLink = result.Links?.Self;
                if (selfLink) {
                    const result2 = this.get<TResource>(requestContext, selfLink);
                    resolve(result2);
                    return;
                }
                resolve(result);
            }, reject);
        });
    }
    update<TResource>(requestContext: RequestContext, path: string, resource: TResource, args?: RouteArgs): Promise<TResource> {
        const url = this.resolve(path, args);
        return new Promise((resolve, reject) => {
            this.dispatchRequest(requestContext, "PUT", url, resource).then((result: any) => {
                const selfLink = result.Links?.Self;
                if (selfLink) {
                    const result2 = this.get<TResource>(requestContext, selfLink);
                    resolve(result2);
                    return;
                }
                resolve(result);
            }, reject);
        });
    }
    del(requestContext: RequestContext, path: string, resource?: any, args?: RouteArgs) {
        const url = this.resolve(path, args);
        return this.dispatchRequest(requestContext, "DELETE", url, resource);
    }
    delete(requestContext: RequestContext, path: string, resource?: any, args?: RouteArgs) {
        const url = this.resolveUrlWithSpaceId(path, args);
        return this.dispatchRequest(requestContext, "DELETE", url, resource);
    }
    put<TResource>(requestContext: RequestContext, path: string, resource?: TResource, args?: RouteArgs): Promise<TResource> {
        const url = this.resolveUrlWithSpaceId(path, args);
        return this.dispatchRequest(requestContext, "PUT", url, resource) as Promise<TResource>;
    }
    getAntiforgeryToken() {
        if (!this.isConnected()) {
            return null;
        }
        const installationId = this.getGlobalRootDocument()!.InstallationId;
        if (!installationId) {
            return null;
        }
        // If we have come this far we know we are on a version of Octopus Server which supports anti-forgery tokens
        const antiforgeryCookieName = "Octopus-Csrf-Token_" + installationId;
        const antiforgeryCookies = document.cookie
            .split(";")
            .filter((c) => {
            return c.trim().indexOf(antiforgeryCookieName) === 0;
        })
            .map((c) => {
            return c.trim();
        });
        if (antiforgeryCookies && antiforgeryCookies.length === 1) {
            const antiforgeryToken = antiforgeryCookies[0].split("=")[1];
            return antiforgeryToken;
        }
        else {
            if (Environment.isInDevelopmentMode()) {
                return "FAKE TOKEN USED FOR DEVELOPMENT";
            }
            return null;
        }
    }
    resolveLinkTemplate(link: GlobalAndSpaceRootLinks, args: any) {
        return this.resolve(this.getLink(link), args);
    }
    getServerInformation() {
        if (!this.isConnected()) {
            throw new Error("The Octopus Client has not connected. THIS SHOULD NOT HAPPEN! Please notify support.");
        }
        return {
            version: this.rootDocument!.Version,
        };
    }
    tryGetServerInformation() {
        return this.rootDocument
            ? {
                version: this.rootDocument.Version,
                installationId: this.rootDocument.InstallationId,
            }
            : null;
    }
    throwIfClientNotConnected() {
        if (!this.isConnected()) {
            const errorMessage = "Can't get a link from the client, because the client has not yet been connected.";
            const extraContextForFunctionalTests = "In your Functional Test, use `setupGlobals.connectClient()` to connect the client, or call `setupGlobals.all(...)`." + " For more information, see https://docs.octopushq.com/docs/frontend/functional_tests#globalambient-context";
            logger.debug(`${errorMessage} ${extraContextForFunctionalTests}`);
            throw new Error(errorMessage);
        }
    }
    getSystemLink<T>(linkGetter: (links: GlobalRootLinks) => T): T {
        this.throwIfClientNotConnected();
        const link = linkGetter(this.rootDocument!.Links);
        if (link === null) {
            const errorMessage = `Can't get a link from the client, because it could not be found in the root document.`;
            throw new Error(errorMessage);
        }
        return link;
    }
    getLink(name: GlobalAndSpaceRootLinks): string {
        this.throwIfClientNotConnected();
        const spaceLinkExists = this.spaceRootDocument && this.spaceRootDocument.Links[name];
        const link = spaceLinkExists ? this.spaceRootDocument!.Links[name] : this.rootDocument!.Links[name];
        if (!link) {
            const errorMessage = `Can't get the link for ${name} from the client, because it could not be found in the root document or the space root document. You may be trying to retrieve a space-scoped link from a client that does not yet have a space context configured.`;
            const extraContextForFunctionalTests = "In your Functional Test, use `setupGlobals.setupSpaceContext(space)` to set up the client's space context, or call `setupGlobals.all(space)`." +
                " For more information, see https://docs.octopushq.com/docs/frontend/functional_tests#globalambient-context";
            logger.debug(`${errorMessage} ${extraContextForFunctionalTests}`);
            throw new Error(errorMessage);
        }
        return link;
    }
    private async dispatchFetch(options: FetchOptions) {
        if (!(await this.checkAuthentication(options.requestContext, options.url))) {
            logger.info("Cancelled request because the user was not authenticated.");
            throw new Error("User was not authenticated.");
        }
        try {
            return await dispatchFetch(options, this.tryGetServerInformation()?.version, () => this.getAntiforgeryToken());
        }
        catch (error) {
            if (error instanceof OctopusError) {
                if (error.StatusCode === 401 && !isAnonymous(options.url) && !isAuthenticationCheck(options.url)) {
                    logger.info("Session Auth Cookie has expired or is missing. Redirecting to login page, but will return to {location}.", { location: window.location });
                    await this.reAuthenticateAndReturn(options.requestContext);
                }
            }
            throw error;
        }
    }
    private async dispatchRequest(requestContext: RequestContext, method: any, url: string, requestBody?: any, raw?: boolean) {
        if (!(await this.checkAuthentication(requestContext, url))) {
            logger.info("Cancelled request because the user was not authenticated.");
            return;
        }
        return await new Promise((resolve, reject) => {
            new Ajax({
                session: this.session,
                error: (e) => {
                    if (e.StatusCode === 401 && !isAnonymous(url) && !isAuthenticationCheck(url)) {
                        logger.info("Session Auth Cookie has expired or is missing. Redirecting to login page, but will return to {location}.", { location: window.location });
                        this.reAuthenticateAndReturn(requestContext).finally(() => reject(e));
                    }
                    else {
                        reject(e);
                    }
                },
                method,
                url,
                requestBody,
                requestContext,
                raw,
                success: (data) => resolve(data),
                tryGetServerInformation: () => this.tryGetServerInformation()!,
                getAntiForgeryTokenCallback: () => this.getAntiforgeryToken()!,
                onRequestCallback: (r) => this.onAjaxRequest(r),
                onResponseCallback: (r) => this.onAjaxResponse(r),
                onErrorResponseCallback: (r) => this.onAjaxErrorResponse(r),
            }).execute();
        });
    }
    private checkAuthentication = async (requestContext: RequestContext, url: string) => {
        // If we don't have an anti-forgery token we need to get one by logging in again
        if (this.session.isAuthenticated() && !this.getAntiforgeryToken() && !isAnonymous(url)) {
            logger.warn("The expected anti-forgery cookie is missing or has expired. Redirecting to login page, but will return to {location}. See {link} for more details and troubleshooting.", {
                location: window.location,
                link: OctoLink.Create("CSRF"),
            });
            await this.reAuthenticateAndReturn(requestContext);
            return false;
        }
        else {
            return true;
        }
    };
    private reAuthenticateAndReturn = async (requestContext: RequestContext) => {
        await new UserRepository(this, requestContext).signOut().then(() => {
            this.session.end();
        });
    };
    isConnected() {
        return this.rootDocument !== null;
    }
    private getArgsWithSpaceId(args: RouteArgs) {
        return this.spaceId ? { spaceId: this.spaceId, ...args } : args;
    }
    private getGlobalRootDocument() {
        if (!this.isConnected()) {
            throw new Error("The Octopus Client has not connected.");
        }
        return this.rootDocument;
    }
    resolveUrlWithSpaceId(path: string, args?: RouteArgs): string {
        return this.resolve(path, this.getArgsWithSpaceId(args!));
    }
    dispatchEvent(event: ClientEvent) {
        this.eventBus.dispatchEvent(event);
    }
    subscribe(eventHandler: EventHandler) {
        return this.eventBus.subscribe(eventHandler);
    }
}
export class ClientSession {
    constructor(readonly cache: Caching, readonly isAuthenticated: () => boolean, private readonly endSession: () => void) { }
    end = () => {
        this.endSession();
        this.cache.clearAll();
    };
}
const isAnonymous = (url: string): boolean => {
    return url.includes("/users/register/") || url.endsWith("/users/login") || url.endsWith("/users/logout") || url.endsWith("/api");
};
const isAuthenticationCheck = (url: string): boolean => {
    return url.endsWith("/users/me");
};
